import React from "react";
import PolicyLayout, { Section, PolicyList } from "../../components/storefront/PolicyLayout";

export default function PrivacyPolicy() {
  return (
    <PolicyLayout
      testid="privacy-policy-page"
      heading="Privacy Policy"
      title="Privacy Policy | Lecis Auto Glass"
      description="How Lecis Auto Glass collects, uses and protects your information — cookies, analytics, Stripe payments, contact forms and customer data handling."
      canonicalPath="/privacy-policy"
      updated="June 2026"
    >
      <Section>
        <p>
          Lecis Auto Glass ("we", "us", "our") respects your privacy. This policy explains what information
          we collect, how we use it, and the choices you have. By using our website you agree to the practices
          described here.
        </p>
      </Section>

      <Section title="Information We Collect">
        <PolicyList
          items={[
            "Information you provide: name, email, shipping/billing address, phone number and order details when you make a purchase or submit a contact/fitment form.",
            "Payment information: card payments are processed securely by our payment processor (Stripe). We do not store full card numbers on our servers.",
            "Automatically collected data: IP address, device/browser type, pages visited and interactions, collected via cookies and analytics.",
          ]}
        />
      </Section>

      <Section title="Cookies">
        <p>
          We use cookies and similar technologies to keep your shopping cart working, remember preferences,
          and understand how the site is used. You can control or disable cookies in your browser settings,
          though some features (such as checkout) may not function properly without them.
        </p>
      </Section>

      <Section title="Analytics">
        <p>
          We use <strong className="text-zinc-100">Google Analytics 4</strong> to understand how visitors use
          our website (for example, which products and pages are popular). Analytics data is aggregated and
          used to improve our store and customer experience. Google may process this data in accordance with
          its own privacy policies.
        </p>
      </Section>

      <Section title="Payments (Stripe)">
        <p>
          Payments are handled by <strong className="text-zinc-100">Stripe</strong>, a PCI-compliant payment
          processor. When you check out, your payment details are transmitted directly to Stripe over an
          encrypted connection. Stripe processes your payment information under its own privacy and security
          policies; we receive only the confirmation and limited details needed to fulfill your order.
        </p>
      </Section>

      <Section title="Contact & Fitment Forms">
        <p>
          When you submit a contact or fitment-assistance form, we use the information you provide (such as
          your name, email and vehicle details) to respond to your inquiry and help verify fitment. We may
          send you email regarding your inquiry or order using our email service provider.
        </p>
      </Section>

      <Section title="SMS / Text Messaging">
        <p>
          If you provide your mobile number and opt in to text messaging — for example, by checking the SMS
          consent box on a contact or fitment form, or by texting us at (888) 785-1288 — we use your number
          only to send conversational messages regarding your inquiry, appointment, order, or account. Message
          frequency may vary and message and data rates may apply. You can reply STOP to opt out at any time,
          or HELP for assistance.
        </p>
        <p>
          <strong className="text-zinc-100">
            No mobile opt-in data will be shared with third parties for marketing or promotional purposes.
          </strong>
        </p>
      </Section>

      <Section title="How We Use Your Information">
        <PolicyList
          items={[
            "To process, fulfill and support your orders.",
            "To respond to inquiries and provide fitment assistance and customer service.",
            "To send order confirmations, shipping updates and service-related emails.",
            "To operate, maintain, secure and improve our website.",
            "To comply with legal obligations and prevent fraud.",
          ]}
        />
      </Section>

      <Section title="Sharing of Information">
        <p>
          We do not sell your personal information. We share information only with trusted service providers
          who help us operate our business — such as payment processing (Stripe), shipping carriers, email
          delivery and analytics — and only as needed to provide those services, or when required by law.
        </p>
      </Section>

      <Section title="Data Retention & Security">
        <p>
          We retain order and inquiry information as needed to provide our services and meet legal, accounting
          and reporting requirements. We use reasonable administrative, technical and physical safeguards to
          protect your information. No method of transmission or storage is 100% secure, but we work to protect
          your data.
        </p>
      </Section>

      <Section title="Your Choices & Contact">
        <p>
          You may request access to, correction of, or deletion of your personal information, or opt out of
          non-essential communications, by contacting us at support@lecisautoglass.com. We will respond in
          accordance with applicable law.
        </p>
      </Section>
    </PolicyLayout>
  );
}
